/** Evasys: SYS / EXAM / ALL */
export const EVASYS_TARGET_PRODUCT_OPTIONS = [
  { value: 'SYS', label: 'SYS' },
  { value: 'EXAM', label: 'EXAM' },
  { value: 'ALL', label: 'ALL' },
];

/** Tenants that use SYS / EXAM / ALL account target_products (same UX as Evasys). */
export const SYS_EXAM_PRODUCT_CONTEXT_TENANT_IDS = ['evasys', 'evasys_v2', 'scantron'];

export function isSysExamProductContextTenant(tenantId) {
  return SYS_EXAM_PRODUCT_CONTEXT_TENANT_IDS.includes(
    String(tenantId ?? '').trim().toLowerCase(),
  );
}

/** Learnwise: SUPPORT / FEEDBACK / TUTOR / ALL (aligned with Settings → Personalization) */
export const LEARNWISE_TARGET_PRODUCT_OPTIONS = [
  { value: 'SUPPORT', label: 'SUPPORT' },
  { value: 'FEEDBACK', label: 'FEEDBACK' },
  { value: 'TUTOR', label: 'TUTOR' },
  { value: 'ALL', label: 'ALL' },
];

/** Default DIY / settings labels when BE or saved rows omit product_name. */
export const CAYUSE_PRODUCT_DISPLAY_NAMES = {
  GRAD: 'GRAD (Haplo)',
  COMPLIANCE: 'COMPLIANCE (Compliance Suite)',
  SPONSOR: 'SPONSOR (Sponsored Projects)',
};

/** Cayuse: ALL / GRAD / COMPLIANCE / SPONSOR */
export const CAYUSE_TARGET_PRODUCT_OPTIONS = [
  { value: 'ALL', label: 'ALL' },
  { value: 'GRAD', label: 'GRAD' },
  { value: 'COMPLIANCE', label: 'COMPLIANCE' },
  { value: 'SPONSOR', label: 'SPONSOR' },
];

/**
 * Blackboard: LMS / INTEGRITY / EVAL / ALL.
 *
 * These three ARE the tenant's three Market lens categories (lms, academic_integrity,
 * course_evaluation), so an account's target product, a Market category filter and a DIY product
 * context all name the same thing. Keys must stay identical to api/src/config/productContexts.ts
 * and admin_atoms.products.product_context — the BE validates writes against that list, so a key
 * that drifts here is rejected with a 400 rather than failing quietly.
 */
export const BLACKBOARD_TARGET_PRODUCT_OPTIONS = [
  { value: 'LMS', label: 'LMS' },
  { value: 'INTEGRITY', label: 'INTEGRITY' },
  { value: 'EVAL', label: 'EVAL' },
  { value: 'ALL', label: 'ALL' },
];

export const BLACKBOARD_KEYS = ['LMS', 'INTEGRITY', 'EVAL'];

/**
 * Unit4: ERP / HCM / ALL.
 *
 * The two keys ARE the tenant's two Market lens categories (finance_erp,
 * talent_management_hcm), the same relationship Blackboard's three have. Keys are copied
 * verbatim from api/src/config/productContexts.ts — the BE validates writes against that
 * list and rejects a drifted key with a 400 rather than failing quietly.
 */
export const UNIT4_TARGET_PRODUCT_OPTIONS = [
  { value: 'ERP', label: 'ERP' },
  { value: 'HCM', label: 'HCM' },
  { value: 'ALL', label: 'ALL' },
];

export const UNIT4_KEYS = ['ERP', 'HCM'];

/**
 * Constructor: PROCTORING / MARKING / ONLINE_EXAMS / LMS / ALL.
 *
 * The four keys ARE the tenant's four Market lens categories, the same relationship
 * Blackboard's three and unit4's two have. Keys copied verbatim from
 * api/src/config/productContexts.ts and matching admin_atoms.products.product_key for
 * this tenant (Constructor Proctor / Assess (AI grading) / Assess / Learn) — the BE
 * validates writes against that list and 400s on a drifted key.
 * ONLINE_EXAMS and MARKING are both Constructor Assess: ONE app covering two jobs
 * (create-and-deliver vs grade-and-review), which is why they are separate contexts.
 */
export const CONSTRUCTOR_TARGET_PRODUCT_OPTIONS = [
  { value: 'PROCTORING',   label: 'PROCTORING' },
  { value: 'MARKING',      label: 'MARKING' },
  { value: 'ONLINE_EXAMS', label: 'ONLINE_EXAMS' },
  { value: 'LMS',          label: 'LMS' },
  { value: 'ALL',          label: 'ALL' },
];

export const CONSTRUCTOR_KEYS = ['PROCTORING', 'MARKING', 'ONLINE_EXAMS', 'LMS'];

export function isConstructorProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'constructor_tech';
}

/** Normalize account.target_products to PROCTORING | MARKING | ONLINE_EXAMS | LMS | ALL | '' */
export function deriveConstructorTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...CONSTRUCTOR_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // More than one lane in play is the portfolio story, which is what ALL means here.
    const picked = CONSTRUCTOR_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

export function isUnit4ProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'unit4';
}

/** Normalize account.target_products to ERP | HCM | ALL | '' */
export function deriveUnit4TargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...UNIT4_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // Both lanes in play is the portfolio story, which is what ALL means here.
    const picked = UNIT4_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

/**
 * Access People: HCM / ERP / ALL.
 *
 * The two keys ARE the tenant's two Market lens categories (talent_management_hcm,
 * finance_erp) — the same relationship unit4's two and Blackboard's three have — and they are
 * copied verbatim from api/src/config/productContexts.ts, which the BE validates writes
 * against. HCM is listed first because PeopleXD leads for this tenant.
 */
export const ACCESS_PEOPLE_TARGET_PRODUCT_OPTIONS = [
  { value: 'HCM', label: 'HCM' },
  { value: 'ERP', label: 'ERP' },
  { value: 'ALL', label: 'ALL' },
];

export const ACCESS_PEOPLE_KEYS = ['HCM', 'ERP'];

/**
 * 4Science: CRIS / REPOSITORY / GLAM / ALL.
 *
 * The three keys ARE the tenant's three Market lens categories (research_information_cris,
 * digital_repository, digital_preservation) — the same relationship unit4's two and Blackboard's
 * three have — copied verbatim from api/src/config/productContexts.ts, which the BE validates
 * writes against and 400s on a drifted key. Each is a DSpace product: DSpace-CRIS, DSpaceWay
 * (the managed DSpace service) and DSpace-GLAM for cultural heritage.
 */
export const FOURSCIENCE_TARGET_PRODUCT_OPTIONS = [
  { value: 'CRIS',       label: 'CRIS' },
  { value: 'REPOSITORY', label: 'REPOSITORY' },
  { value: 'GLAM',       label: 'GLAM' },
  { value: 'ALL',        label: 'ALL' },
];

export const FOURSCIENCE_KEYS = ['CRIS', 'REPOSITORY', 'GLAM'];

export function isFourScienceProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === '4science';
}

/** Normalize account.target_products to CRIS | REPOSITORY | GLAM | ALL | '' */
export function deriveFourScienceTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...FOURSCIENCE_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // More than one lane in play is the portfolio story, which is what ALL means here.
    const picked = FOURSCIENCE_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

/**
 * TestWe: ONLINE_EXAMS / PROCTORING / MARKING / ALL. [2026-10-18]
 *
 * The three keys ARE the tenant's three Market lens categories (online_exams, proctoring,
 * marking) — the same relationship constructor_tech's have — copied verbatim from
 * api/src/config/productContexts.ts and admin_atoms.products.product_key (TestWe Assessments,
 * ProctorWe, TestWe AI Assistant). The BE validates writes against that list and 400s on a drift.
 */
export const TESTWE_TARGET_PRODUCT_OPTIONS = [
  { value: 'ONLINE_EXAMS', label: 'ONLINE_EXAMS' },
  { value: 'PROCTORING',   label: 'PROCTORING' },
  { value: 'MARKING',      label: 'MARKING' },
  { value: 'ALL',          label: 'ALL' },
];

export const TESTWE_KEYS = ['ONLINE_EXAMS', 'PROCTORING', 'MARKING'];

export function isTestweProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'testwe';
}

/** Normalize account.target_products to ONLINE_EXAMS | PROCTORING | MARKING | ALL | '' */
export function deriveTestweTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...TESTWE_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // More than one lane in play is the platform story, which is what ALL means here.
    const picked = TESTWE_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

export function isAccessPeopleProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'access_people';
}

/** Normalize account.target_products to HCM | ERP | ALL | '' */
export function deriveAccessPeopleTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...ACCESS_PEOPLE_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // Both lanes in play is the portfolio story, which is what ALL means here.
    const picked = ACCESS_PEOPLE_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

export function isBlackboardProductContextTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'blackboard';
}

/** Normalize account.target_products to LMS | INTEGRITY | EVAL | ALL | '' */
export function deriveBlackboardTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...BLACKBOARD_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    // More than one lane selected is the portfolio story, which is what ALL means here.
    const picked = BLACKBOARD_KEYS.filter((k) => normalized.includes(k));
    if (picked.length > 1) return 'ALL';
    if (picked.length === 1) return picked[0];
    return normalized[0] || '';
  }
  return '';
}

const LEARNWISE_KEYS = ['SUPPORT', 'FEEDBACK', 'TUTOR'];
export const CAYUSE_KEYS = ['GRAD', 'COMPLIANCE', 'SPONSOR'];

/** Normalize account.target_products to SYS | EXAM | ALL | '' */
export function deriveEvasysTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return ['SYS', 'EXAM', 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    if (normalized.includes('SYS') && normalized.includes('EXAM')) return 'ALL';
    if (normalized.includes('SYS')) return 'SYS';
    if (normalized.includes('EXAM')) return 'EXAM';
    return normalized[0] || '';
  }
  return '';
}

/** Normalize account.target_products to SUPPORT | FEEDBACK | TUTOR | ALL | '' */
export function deriveLearnwiseTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...LEARNWISE_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    if (LEARNWISE_KEYS.every((k) => normalized.includes(k))) return 'ALL';
    if (normalized.includes('SUPPORT')) return 'SUPPORT';
    if (normalized.includes('FEEDBACK')) return 'FEEDBACK';
    if (normalized.includes('TUTOR')) return 'TUTOR';
    return normalized[0] || '';
  }
  return '';
}

export function isPlaceholderProductName(name) {
  const s = String(name ?? '').trim();
  return !s || /^unnamed\s+product$/i.test(s);
}

/** Resolve UI label for a product_key (Settings, DIY playbook alignment). */
export function getProductDisplayNameForTenant(tenantId, productKey, preferredName) {
  if (!isPlaceholderProductName(preferredName)) {
    return String(preferredName).trim();
  }
  const pk = String(productKey ?? '').trim().toUpperCase();
  const tenant = String(tenantId ?? '').toLowerCase();
  if (tenant === 'cayuse' && CAYUSE_PRODUCT_DISPLAY_NAMES[pk]) {
    return CAYUSE_PRODUCT_DISPLAY_NAMES[pk];
  }
  if (pk) return pk;
  return 'Product';
}

/** Normalize account.target_products to GRAD | COMPLIANCE | SPONSOR | ALL | '' */
export function deriveCayuseTargetProductValue(targetProducts) {
  if (targetProducts == null) return '';
  if (typeof targetProducts === 'string') {
    const s = targetProducts.trim().toUpperCase();
    return [...CAYUSE_KEYS, 'ALL'].includes(s) ? s : s || '';
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) {
    const normalized = targetProducts.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return 'ALL';
    if (CAYUSE_KEYS.every((k) => normalized.includes(k))) return 'ALL';
    if (normalized.includes('GRAD')) return 'GRAD';
    if (normalized.includes('COMPLIANCE')) return 'COMPLIANCE';
    if (normalized.includes('SPONSOR')) return 'SPONSOR';
    return normalized[0] || '';
  }
  return '';
}

/**
 * Evaexam integrity playbook: show only accounts whose target_products resolve to EXAM or ALL
 * (literal ALL, or both SYS+EXAM per deriveEvasysTargetProductValue).
 */
export function isEvasysAccountEligibleForExamIntegrityPlaybook(targetProducts) {
  const d = deriveEvasysTargetProductValue(targetProducts);
  return d === 'EXAM' || d === 'ALL';
}

/**
 * Evasys governance playbook: show only accounts whose target_products resolve to SYS or ALL.
 */
export function isEvasysAccountEligibleForSysGovernancePlaybook(targetProducts) {
  const d = deriveEvasysTargetProductValue(targetProducts);
  return d === 'SYS' || d === 'ALL';
}

/** United States / Canada (Evasys: default product focus EXAM when both SYS and EXAM apply). */
export function isEvasysNorthAmericaCountry(country, countryName) {
  const normalized = String(country ?? countryName ?? '')
    .trim()
    .toLowerCase();
  return (
    normalized === 'united states' ||
    normalized === 'united states of america' ||
    normalized === 'usa' ||
    normalized === 'us' ||
    normalized === 'canada'
  );
}

/**
 * Discrete SYS/EXAM options for playbook preview (matches PlaybookPreview parsing).
 * ALL or both products → ['SYS','EXAM'].
 */
export function getEvasysTargetProductListForContext(raw) {
  if (raw == null) return [];
  if (typeof raw === 'string' && raw.trim()) {
    const u = raw.trim().toUpperCase();
    if (u === 'ALL') return ['SYS', 'EXAM'];
    if (u === 'SYS' || u === 'EXAM') return [u];
    return [u];
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const normalized = raw.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return ['SYS', 'EXAM'];
    const hasSys = normalized.includes('SYS');
    const hasExam = normalized.includes('EXAM');
    if (hasSys && hasExam) return ['SYS', 'EXAM'];
    if (hasSys) return ['SYS'];
    if (hasExam) return ['EXAM'];
    return normalized;
  }
  return [];
}

/**
 * Discrete GRAD / COMPLIANCE / SPONSOR lanes for playbook preview, outreach filtering, persona affinity.
 * ALL or all three keys → ['GRAD','COMPLIANCE','SPONSOR'].
 */
export function getCayuseTargetProductListForContext(raw) {
  if (raw == null) return [];
  if (typeof raw === 'string' && raw.trim()) {
    const u = raw.trim().toUpperCase();
    if (u === 'ALL') return [...CAYUSE_KEYS];
    if (CAYUSE_KEYS.includes(u)) return [u];
    return [u];
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const normalized = raw.map((p) => String(p).trim().toUpperCase()).filter(Boolean);
    if (normalized.includes('ALL')) return [...CAYUSE_KEYS];
    if (CAYUSE_KEYS.every((k) => normalized.includes(k))) return [...CAYUSE_KEYS];
    const lanes = CAYUSE_KEYS.filter((k) => normalized.includes(k));
    if (lanes.length > 0) return lanes;
    return normalized;
  }
  return [];
}

/** Initial selectedProductContexts for generic Cayuse playbooks (multi-lane accounts only). */
export function getCayuseDefaultSelectedProductContexts(raw) {
  const products = getCayuseTargetProductListForContext(raw);
  if (products.length <= 1) return [];
  return [...products];
}

/**
 * Initial `selectedProductContexts` for generic Evasys playbooks (multi-product only).
 * US / Canada: default EXAM instead of both SYS and EXAM.
 */
export function getEvasysDefaultSelectedProductContexts(raw, country, countryName) {
  const products = getEvasysTargetProductListForContext(raw);
  if (products.length <= 1) return [];
  if (isEvasysNorthAmericaCountry(country, countryName)) return ['EXAM'];
  return [...products];
}
